import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Badge, Modal, Pagination } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import { getAllLeaveApplications, cancelLeave, getAllLeaveTypes } from "../../../../api/onboarddeboard";
const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};
const STATUS_VARIANT = { PENDING: "primary", APPROVED: "success", REJECTED: "danger", CANCELLED: "secondary" };
const ITEMS_PER_PAGE = 20;

const LeaveCancellation = () => {
  const token = localStorage.getItem("token");
  const roleId = parseInt(localStorage.getItem("roleId"));
  const isAdmin = [1, 2].includes(roleId);

  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
 const [filterLeaveTypeId, setFilterLeaveTypeId] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchLeaveTypes = useCallback(() => {
    getAllLeaveTypes(token).then((r) => setLeaveTypes((r.data || []).filter((l) => l.status))).catch(() => { });
  }, [token]);

  const fetchAllLeaves = useCallback(() => {
    setIsLoading(true);
    getAllLeaveApplications(token)
      .then((r) => setApplications(r.data || []))
      .catch(() => toast.error("Error loading leave applications."))
      .finally(() => setIsLoading(false));
  }, [token]);

  useEffect(() => {
    if (token && isAdmin) { fetchLeaveTypes(); fetchAllLeaves(); }
  }, [token, isAdmin, fetchLeaveTypes, fetchAllLeaves]);

  if (!isAdmin) return <Navigate to="/reporting/Dashboard" replace />;

  let filtered = applications.filter((item) => item.status !== "REJECTED");
  if (filterLeaveTypeId) filtered = filtered.filter((item) => String(item.leaveTypeId) === String(filterLeaveTypeId));
  if (filterFromDate) filtered = filtered.filter((item) => item.fromDate && item.fromDate >= filterFromDate);
  if (filterToDate) filtered = filtered.filter((item) => item.toDate && item.toDate <= filterToDate);
  if (searchTerm.trim()) {
    const s = searchTerm.toLowerCase();
    filtered = filtered.filter((item) => item.employeeName?.toLowerCase().includes(s) || item.leaveTypeName?.toLowerCase().includes(s) || item.reason?.toLowerCase().includes(s));
  }
  if (statusFilter !== "ALL") filtered = filtered.filter((item) => item.status === statusFilter);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleResetFilters = () => {
    setFilterLeaveTypeId(""); setFilterFromDate(""); setFilterToDate(""); setSearchTerm(""); setStatusFilter("ALL");
  };

  const handleCancelClick = (item) => { setSelectedLeave(item); setShowModal(true); };

  const handleConfirmCancel = async () => {
    if (!selectedLeave) return;
    setIsCancelling(true);
    try {
      await cancelLeave(selectedLeave.id, token);
      toast.success("Leave cancelled successfully.");
      setShowModal(false); setSelectedLeave(null);
      fetchAllLeaves();
    } catch (err) {
      toast.error(err?.response?.data || "Failed to cancel leave.");
    } finally {
      setIsCancelling(false);
    }
  };

  const canCancel = (status) => status === "PENDING";

  const handleExportCSV = () => {
    const headers = ["Employee", "Leave Type", "Application Date", "FromDate", "ToDate", "Total Days", "Status"];
    let csv = headers.join(",") + "\n";
    filtered.forEach((item) => {
      csv += [item.employeeName, item.leaveTypeName, item.createdAt?.split("T")[0] || "", item.fromDate, item.toDate, item.totalDays, item.status]
        .map((c) => `"${c ?? ""}"`).join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `leave-cancellation-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Container fluid className="p-4">
      <style>{`
        .leave-toggle .form-check-input:checked { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
      `}</style>
      <ToastContainer position="top-right" autoClose={3000} />

      <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Leave Cancellation</h4>
      <Breadcrumb className="lc-breadcrumb">
        <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
        <Breadcrumb.Item href="#">Leave Application</Breadcrumb.Item>
        <Breadcrumb.Item active>Leave Cancellation</Breadcrumb.Item>
      </Breadcrumb>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{ fontSize: 16 }}>Confirm Cancellation</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedLeave && (
            <>
              <p style={{ marginBottom: 12 }}>Are you sure you want to cancel this leave?</p>
              <table style={{ fontSize: 13, width: "100%" }}>
                <tbody>
                  {[["Employee", selectedLeave.employeeName], ["Leave Type", selectedLeave.leaveTypeName], ["From", selectedLeave.fromDate], ["To", selectedLeave.toDate]].map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ color: "#666", paddingBottom: 6, width: 110 }}>{label}</td>
                      <td style={{ fontWeight: 500 }}>{value || "-"}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ color: "#666" }}>Status</td>
                    <td><Badge bg={STATUS_VARIANT[selectedLeave.status] || "secondary"}>{selectedLeave.status}</Badge></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isCancelling}>No, Go Back</Button>
          <Button variant="danger" onClick={handleConfirmCancel} disabled={isCancelling}>{isCancelling ? "Cancelling..." : "Yes, Cancel Leave"}</Button>
        </Modal.Footer>
      </Modal>

      <Row className="mb-4">
        <Col xl={12}>
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Row className="mb-3">
                    <Form.Label className="col-sm-4 col-form-label form-label">Leave Type</Form.Label>
                    <Col sm={8}>
                      <Form.Select value={filterLeaveTypeId} onChange={(e) => setFilterLeaveTypeId(e.target.value)}>
                        <option value="">All Leave Types</option>
                        {leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.leaveName}</option>)}
                      </Form.Select>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Row className="mb-3">
                    <Form.Label className="col-sm-4 col-form-label form-label">From Date</Form.Label>
                    <Col sm={8}>
                      <DatePicker
                        selected={filterFromDate ? new Date(filterFromDate) : null}
                        onChange={(date) => {
                          setFilterFromDate(date ? date.toISOString().split("T")[0] : "");
                          setFilterToDate("");
                        }}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        wrapperClassName="d-block"
                        placeholderText="dd/mm/yyyy"
                        isClearable
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Row className="mb-3">
                    <Form.Label className="col-sm-4 col-form-label form-label">To Date</Form.Label>
                    <Col sm={8}>
                      <DatePicker
                        selected={filterToDate ? new Date(filterToDate) : null}
                        onChange={(date) => setFilterToDate(date ? date.toISOString().split("T")[0] : "")}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        wrapperClassName="d-block"
                        placeholderText="dd/mm/yyyy"
                        minDate={filterFromDate ? new Date(filterFromDate) : undefined}
                        isClearable
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col md={6} className="d-flex justify-content-center">
                  <Button variant="secondary" onClick={handleResetFilters} style={{ fontSize: 13 }}>Cancel</Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">All Employee Leave Applications</h5>
              <i
                className="bi bi-download"
                title="Export CSV"
                style={{ fontSize: "1.3rem", cursor: "pointer", color: "#2E5CB8" }}
                onClick={handleExportCSV}
              />
            </Card.Header>
            <Card.Body>
              <Row className="mb-3 justify-content-end align-items-center g-2">
                <Col xs="auto">
                  <Form.Control placeholder="Search employee, leave type..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ width: 260 }} />
                </Col>
                <Col xs="auto">
                  <Form.Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} style={{ width: 150 }}>
                    <option value="ALL">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="CANCELLED">Cancelled</option>
                  </Form.Select>
                </Col>
                <Col xs="auto"><Badge bg="secondary">Total: {filtered.length}</Badge></Col>
              </Row>

              <Table bordered hover responsive>
                <thead>
                  <tr>
                    {["Sr.No", "Employee", "Leave Type", "Application Date", "FromDate", "ToDate", "Total Days", "Status", "Action"].map((h) => (
                      <th key={h} style={{ backgroundColor: "#2E5CB8", color: "white", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && <tr><td colSpan={9} className="text-center py-4"><div className="text-muted">Loading...</div></td></tr>}
                  {!isLoading && paginated.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-4"><div className="text-muted">No leave applications found</div></td></tr>
                  )}
                  {!isLoading && paginated.map((item, index) => (
                    <tr key={item.id}>
                      <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{item.employeeName || "-"}</td>
                      <td><span className="badge" style={{ backgroundColor: item.leaveTypeColor || "#2E5CB8" }}>{item.leaveTypeName}</span></td>
                      <td>{fmtDate(item.createdAt?.split("T")[0])}</td>
                      <td>{fmtDate(item.fromDate)}</td>
                      <td>{fmtDate(item.toDate)}</td>
                      <td>{item.totalDays ?? "-"}</td>
                      <td><Badge bg={STATUS_VARIANT[item.status] || "secondary"}>{item.status}</Badge></td>
                      <td>
                        {canCancel(item.status) ? (
                          <Button variant="danger" size="sm" onClick={() => handleCancelClick(item)}>Cancel</Button>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end">
                  <Pagination>
                    <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {[...Array(totalPages).keys()].map((n) => (
                      <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>{n + 1}</Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LeaveCancellation;